class SlideOutModal extends HTMLElement {
	static stack = [];

	constructor() {
		super();

		const zIndex = this.getAttribute('z-index') ?? 10000;

		this.style.position = 'absolute'; // Prevents affecting layout of parent
		this.style.width = '100%';

		this.desktopLayout = this.getAttribute('desktop');
		this.mobileLayout = this.getAttribute('mobile');

		this.setAttribute('role', 'dialog');

		const html = /* html */`
			<style>
				.content {
					position: fixed;
					left: 0;
					right: 0;
					bottom: 0; 
					background-color: white;
					z-index: ${zIndex};
					padding: 16px;
					padding-top: 36px;
					padding-bottom: 40px;
					border-radius: 8px 8px 0px 0px;
					transform: translateY(var(--offset));
					display: flex;
					flex-direction: column;
				}

				:host([no-padding]) .content {
					padding: 0;
					padding-top: 28px;
				}

				.content::before {
					content: '';
					position: absolute;
					display: block;
					width: 64px;
					height: 4px;
					border-radius: 2px;
					top: 16px;
					left: calc(50% - 32px);
					background: var(--color-border-border-primary, #E1E1E1);;
				}

				.bg {
					position: fixed;
					left: 0;
					right: 0;
					bottom: 0;
					top: 0;
					background: var(--overlay, rgba(0, 0, 0, 0.20));
					z-index: ${zIndex};
				}

				@keyframes fade-out {
					from { opacity: 1; }
					to { opacity: 0; }
				}

				@keyframes slide-out {
					from { 
						transform: translateY(0);
						opacity: 1;
					}
					to { 
						transform: translateY(30%); 
						opacity: 0;
					}
				}

				@keyframes swipe-out {
					0% {
						transform: translateY(var(--offset, 0));
					}
					100% {
						transform: translateY(100%);
					}
				}

				.transition-out, .transition-in {
					pointer-events: none;
				}

				.transition-out .bg {
					animation: fade-out 150ms ease-in both;
				}

				.transition-out .content {
					animation: slide-out 150ms ease-in both;
				}

				.transition-out .content.swipe-out {
					animation: swipe-out 150ms ease-in both;
				}

				.transition-in .bg {
					animation: fade-out 250ms ease-in reverse both;
				}

				.transition-in .content {
					animation: slide-out 250ms ease-in reverse both;
				}

				button[name="close"] {
					display: flex;
					border: none;
					background: none;
					width: 24px;
					height: 24px;
					position: absolute;
					top: 8px;
					right: 8px;
					padding: 0;
					align-items: center;
					justify-content: center;
					cursor: pointer;
				}

				button[name="close"] svg {
					width: 100%;
					height: 100%;
				}

				@media screen and (min-width: 992px) {
					@keyframes slide-out {
						from { 
							transform: translateX(0);
						}
						to { 
							transform: translateX(100%); 
						}
					}

					.content {
						left: auto;
						top: 0;
						border-radius: 4px 0px 0px 4px;
						box-shadow: 0px 8px 12px 0px rgba(0, 0, 0, 0.12); 
						padding: 40px;
						max-height: 100vh;
					}

					:host([no-padding]) .content {
						padding: 0 !important;
					}

					.content::before {
						display: none;
					}

					button[name="close"] {
						display: flex;
					}

					:host([desktop="tooltip"]) .bg {
						display: none;
					}

					:host([desktop="tooltip"]) .content {
						animation: none !important;
						position: absolute;
						top: calc(100% + 8px);
						left: calc(50% + var(--tooltip-offset, 0));
						width: max-content;
						max-width: 250px;
						bottom: auto;
						border-radius: 2px;
						background: rgb(240, 240, 240);
						filter: invert(1);
						padding: 8px 12px;
						padding-right: 28px;
						transform: translateX(-50%);
						z-index: 100;
						scroll-margin-top: calc(var(--header-height) + 16px);
					}

					:host([desktop="tooltip"]) .content::before {
						display: block;
						position: absolute;
						top: -3px;
						left: calc(50% - var(--tooltip-offset, 0px));
						content: '';
						border-radius: 0;
						transform: translateX(-50%) rotate(45deg);
						width: 6.485px;
						height: 6.485px; 
						background: rgb(240, 240, 240);
					}

					:host([desktop="tooltip"][tooltip-position="top"]) .content {
						top: auto;
						bottom: calc(100% + 8px);
					}

					:host([desktop="tooltip"][tooltip-position="top"]) .content::before {
						top: auto;
						bottom: -3px;
					}

					:host([desktop="tooltip"]) .transition-out .content {
						display: none;
					}

					:host([desktop="tooltip"]) button[name="close"] {
						transform: scale(0.5);
						transform-origin: top right;
					}

					:host([desktop="cart"]) .content {
						border-radius: 0;
						max-width: 450px;
					}

					:host([desktop="cart"]) button[name="close"] {
						width: 32px;
						height: 32px;
					}

					@keyframes dropdown {
						0% {
							opacity: 0;
							transform: translateY(-5px);
							clip-path: inset(-10px -10px 105% -10px);
						}
						100% {
							opacity: 1;
							transform: translateY(0px);
							clip-path: inset(-10px -10px -10px -10px);
						}
					}

					:host([desktop="dropdown"]) {
						position: relative;
					}

					:host([desktop="dropdown"]) .bg {
						display: none;
					}

					:host([desktop="dropdown"]) .content {
						position: absolute;
						top: 0;
						left: 0;
						right: 0;
						bottom: auto;
						padding: var(--padding-sm);
						border-radius: 2px;
						box-shadow: 0px 8px 12px 0px rgba(0, 0, 0, 0.12);
						width: ${this.getAttribute('dropdown-width') || 'auto'};
						box-sizing: border-box;
					}

					:host([desktop="dropdown"][dropdown-border="true"]) .content {
						border: 1px solid var(--color-border-border-primary, #E1E1E1);
					}

					:host([desktop="dropdown"]) button[name="close"] {
						display: none;
					}

					:host([desktop="dropdown"]) .transition-in .content {
						animation: dropdown 250ms both;
					}

					:host([desktop="dropdown"]) .transition-out .content {
						animation: dropdown 150ms reverse both;
					}

					@keyframes popup {
						from { 
							transform: translate(-50%, calc(-50% + 16px));
							opacity: 0;
						}
						to { 
							transform: translate(-50%, -50%);
							opacity: 1;
						}
					}

					:host([desktop="popup"]) .content {
						top: 50%;
						left: 50%;
						transform: translate(-50%, -50%);
						right: auto;
						bottom: auto;
						border-radius: 4px;
						width: max-content;
					}

					:host([desktop="popup"]) .transition-in .content {
						animation: popup 250ms both;
					}

					:host([desktop="popup"]) .transition-out .content {
						animation: popup 150ms reverse both;
					}
				}

				@media screen and (max-width: 992px) {
					@keyframes slide-out-right {
						from { 
							transform: translateX(0);
						}
						to { 
							transform: translateX(100%); 
						}
					}

					:host([mobile="cart"]) .content {
						top: 0;
						right: 0;
						left: auto;
						border-radius: 0;
						padding-top: 0;
						max-height: 100vh;
						max-width: 450px;
					}

					:host([mobile="cart"]) .content::before {
						display: none;
					}

					:host([mobile="cart"]) button[name="close"] {
						display: block;
						width: 32px;
						height: 32px;
					}

					:host([mobile="cart"]) .transition-out .content {
						animation: slide-out-right 150ms ease-in both;
					}

					:host([mobile="cart"]) .transition-in .content {
						animation: slide-out-right 250ms ease-in reverse both;
					}
				}
			</style>
			<main>
				<div class="bg"></div>
				<div class="content">
					<slot></slot>
					<button aria-label="Close" name="close">
						<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M6.75729 17.2426L11.9999 12M17.2426 6.75736L11.9999 12M11.9999 12L6.75729 6.75736M11.9999 12L17.2426 17.2426" stroke="#575757" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>		
					</button>
				</div>
			</main>
		`

		this.attachShadow({ mode: 'open' });
		this.shadowRoot.innerHTML = html;
		this.style.display = 'none';

		this.name = this.getAttribute('name') || 'Modal';
		this.group = this.getAttribute('group') || undefined;

		this.content = this.shadowRoot.querySelector('.content');
		this.main = this.shadowRoot.querySelector('main');
		this.isOpen = false;

		this.closeOnClickOut = e => {
			if (this.isOpen && 
				!e.composedPath().includes(this.content) && 
				(e.target.parentNode != this.parentNode || e.target == this) &&
				(SlideOutModal.stack[SlideOutModal.stack.length - 1] == this || this.desktopLayout == 'tooltip')
			) {
				this.close('Background');
			}
		}

		this.shadowRoot.querySelector('button[name="close"]').addEventListener('click', () => {
			this.close('Button')
		});

		this.registerDragLogic();

		if (this.getAttribute('open') != null) this.open();
	}

	toggle() {
		if (this.isOpen) this.close();
		else this.open();
	}

	close(source = null) {
		window.removeEventListener('click', this.closeOnClickOut);

		if (!this.isOpen) return;
		this.isOpen = false;

		this.main.classList.add('transition-out');
		const openParent = this.openParent || this.parentElement;
		if (openParent) openParent.classList.remove(this.openClass || (this.id ? `${this.id}-open` : 'modal-open'));

		this.dispatchEvent(new CustomEvent('modalstartclose', { bubbles: true, composed: true }));
		if (source && window.trackEvent) window.trackEvent(this.group, `${this.name}-Close-${source}`);
		if (window.trackFSEvent && this.name != 'Modal') {
			window.trackFSEvent('Modal Close', {
				name: this.name,
				source: source
			});
		}

		setTimeout(() => {
			this.style.display = 'none';

			// Move the modal back to where it was authored (it was portaled to <body>
			// on open). Guard against the original slot having been re-rendered away.
			if (this.portalPlaceholder) {
				if (this.portalParent && this.portalPlaceholder.isConnected) {
					this.portalParent.insertBefore(this, this.portalPlaceholder);
				}
				this.portalPlaceholder.remove();
				this.portalPlaceholder = null;
				this.portalParent = null;
			}

			this.main.classList.remove('transition-out');
			document.documentElement.classList.remove('modal-open');
			this.dispatchEvent(new CustomEvent('modalclose', { bubbles: true, composed: true }));
			this.removeAttribute('open');

			// Restore layouts
			if (this.desktopLayout) this.setAttribute('desktop', this.desktopLayout);
			else this.removeAttribute('desktop');

			if (this.mobileLayout) this.setAttribute('mobile', this.mobileLayout);
			else this.removeAttribute('mobile');

			SlideOutModal.stack = SlideOutModal.stack.filter(modal => modal != this);
		}, 300);
	}

	open({desktop, mobile} = {}) {
		if (this.isOpen) return;
		this.setAttribute('open', '');
		this.isOpen = true;
		this.content.style.setProperty('--tooltip-offset', '0px');

		// Set layout if specified
		if (desktop) this.setAttribute('desktop', desktop);
		if (mobile) this.setAttribute('mobile', mobile);

		// A fixed-layout modal (mobile sheet, or desktop cart/popup/default) renders its
		// content position:fixed; tooltip/dropdown layouts stay anchored in place.
		const isFixedLayout = window.innerWidth < 992 || (this.getAttribute('desktop') != 'tooltip' && this.getAttribute('desktop') != 'dropdown');

		if (isFixedLayout) {
			document.documentElement.classList.add('modal-open');
		}

		// Toggle the open class on the trigger's real parent (capture it before any
		// portaling below moves this element out from under it).
		this.openClass = this.id ? `${this.id}-open` : 'modal-open';
		this.openParent = this.parentElement;
		if (this.openParent) this.openParent.classList.add(this.openClass);
		this.style.display = 'block';

		// iOS Safari clips a position:fixed element to the nearest ancestor that is a
		// scroll container / has overflow:hidden (e.g. .cart-item inside the scrolling
		// .cart-main), so the modal gets trapped inside the cart row. Portal fixed-layout
		// modals to <body> so they're truly viewport-relative. Anchored tooltip/dropdown
		// layouts must stay put, so they're excluded. This is a cart-drawer-specific fix,
		// so only portal modals that live inside the cart drawer.
		if (isFixedLayout && this.parentElement && this.parentElement != document.body && this.closest('cart-drawer')) {
			this.portalPlaceholder = document.createComment('slide-out-modal');
			this.portalParent = this.parentElement;
			this.parentElement.insertBefore(this.portalPlaceholder, this);
			document.body.appendChild(this);
		}

		if (!Shopify.designMode) {
			SlideOutModal.stack.push(this);

			this.main.classList.add('transition-in');
			setTimeout(() => {
				this.main.classList.remove('transition-in');
				window.addEventListener('click', this.closeOnClickOut);
			}, 300);
		
			this.dispatchEvent(new CustomEvent('modalopen', { bubbles: true, composed: true }));
			if (window.trackEvent) window.trackEvent(this.group, `${this.name}-Open`);
			if (window.trackFSEvent && this.name != 'Modal') {
				window.trackFSEvent('Modal Open', {
					name: this.name
				});
			}

		}

		const contentBounds = this.content.getBoundingClientRect();
		const scrollable = this.closest('ul');
		const scrollableBounds = scrollable && window.getComputedStyle(scrollable).overflowX == 'scroll' ? scrollable.getBoundingClientRect() : null;
		const limit = (scrollableBounds ? scrollableBounds.right : window.innerWidth);
		if (contentBounds.right > limit) this.content.style.setProperty('--tooltip-offset', `${limit - contentBounds.right - 16}px`);
		
		this.content.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
	}

	registerDragLogic() {
		if (this.getAttribute('mobile') == 'cart') return;

		let popup = this.shadowRoot.querySelector('.content');
		let dragging = false;
		let dragStart = 0;
		let offset = 0;

		// Drag to close logic for mobile
		popup.addEventListener('touchstart', e => {
			let touchPos = e.touches[0].clientY - popup.offsetTop;
			if (touchPos >= 0 && touchPos <= 40) {
				dragging = true;
				dragStart = touchPos;
			}
		});

		popup.addEventListener('touchmove', e => {
			if (dragging) {
				popup.style.transition = '';
				let touchPos = e.touches[0].clientY - popup.offsetTop;
				offset = touchPos - dragStart;
				popup.style.setProperty('--offset', `${Math.max(0, offset)}px`);
			}
		});

		popup.addEventListener('touchend', e => {
			if (dragging) {
				dragging = false;

				// Close dialog if user has moved it far down enough
				if (offset / popup.clientHeight > 0.2) {
					popup.classList.add('swipe-out');

					this.close();

					setTimeout(() => {
						popup.classList.remove('swipe-out');
						popup.style.removeProperty('--offset');
					}, 500);
				}
				// Otherwise, move it back up
				else {
					popup.style.transition = 'transform 250ms';
					popup.style.removeProperty('--offset');
				}

				dragStart = 0;
				offset = 0;
			}
		});
	}
}

customElements.define('slide-out-modal', SlideOutModal); 


class ExitIntentModal extends SlideOutModal {
	constructor() {
		super();

		this.storageKey = `exit-intent-shown-${window.Shopify.theme.id}`;
		if (localStorage.getItem(this.storageKey) && window.location.host !== '127.0.0.1:9292') return;

		this.shown = false;

		let prev = 0;
		let handle = null;
		let lastInteraction = null;

		window.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') this.show();
		});

		if (!document.referrer.includes('bedthreads.co')) {
			const addTrigger = () => {
				window.history.pushState({}, '');
				window.addEventListener('popstate', () => this.show(), { once: true });
			}

			const onScroll = e => {
				if (e.isTrusted) {
					addTrigger();
					window.removeEventListener('scroll', onScroll);
				}
			}

			window.addEventListener('scroll', onScroll, { passive: true });	
		}

		window.addEventListener('mousemove', e => {
			if (handle) {
				clearTimeout(handle);
				handle = null;
			}

			const current = e.clientY;

			const change = current - prev;
			if (current + change < 5 && !handle) {
				handle = setTimeout(() => this.show());
			}

			prev = current;
			lastInteraction = Date.now();
		}, { passive: true });

		window.addEventListener('scroll', () => {
			lastInteraction = Date.now();
		}, { passive: true });

		window.addEventListener('touchstart', () => {
			lastInteraction = Date.now();
		}, { passive: true });

		window.addEventListener('click', () => {
			lastInteraction = Date.now();
		}, { passive: true });

		const timeout = this.hasAttribute('timeout') ? parseInt(this.getAttribute('timeout')) * 1000 : 5000;

		const intervalHandle = window.setInterval(() => {
			if (lastInteraction && Date.now() - lastInteraction > timeout) {
				this.show();
				clearInterval(intervalHandle);
			}
		}, 500);
	}

	show() {
		if (this.shown) return;
		this.open();
		this.shown = true;
		setTimeout(() => localStorage.setItem(this.storageKey, 'true'), 2000);
	}
}

customElements.define('exit-intent-modal', ExitIntentModal);


class InactivityModal extends SlideOutModal {
	constructor() {
		super();

		this.elapsed = 0;

		this.redirectUrl = this.getAttribute('redirect-url');
		this.openAfter = this.hasAttribute('open-after') ? parseInt(this.getAttribute('open-after')) : 30; // Default to 30 seconds
		this.timeout = this.hasAttribute('timeout') ? parseInt(this.getAttribute('timeout')) : 30; // Default to 30 seconds

		this.param = this.getAttribute('url-param');

		if (this.param && this.param.length > 0) {
			const params = new URLSearchParams(window.location.search);

			// Keep activated for entire session if param is set to true
			if (params.get(this.param) === 'true' || sessionStorage.getItem(this.id) === 'true') {
				this.start();
				sessionStorage.setItem(this.id, 'true');
			}
		}
		else this.start();
	}

	start() {
		setInterval(() => {
			if (Shopify.designMode) return;
			this.elapsed += 1;

			if (this.elapsed >= this.openAfter) {
				if (!this.isOpen) this.open();
				const remaining = Math.max(0, this.timeout - (this.elapsed - this.openAfter));
				if (remaining < 60) this.querySelector('.inactivity-modal__timer').textContent = `(${remaining}s)`;
			}

			if (this.open && this.elapsed >= this.openAfter + this.timeout) {
				window.location.href = this.redirectUrl;
			}
		}, 1000);

		window.addEventListener('mousemove', () => this.cancel(), { passive: true });
		window.addEventListener('scroll', () => this.cancel(), { passive: true });
		window.addEventListener('touchstart', () => this.cancel(), { passive: true });
		window.addEventListener('click', () => this.cancel(), { passive: true });

		this.querySelector('.inactivity-modal__confirm-button').addEventListener('click', () => this.close());
	}

	cancel() {
		this.elapsed = 0;
		this.querySelector('.inactivity-modal__timer').textContent = ``;
	}

	close(source = null) {
		this.elapsed = 0; // Reset elapsed time on close
		super.close(source);
	}
}

customElements.define('inactivity-modal', InactivityModal);